// ============= CATEGORY-SCOPED DASHBOARD DATA =============
// Same wire contract as useDashboardData (one POST to `dashboard-data` → one
// complete filled DashboardSpec, no normalizePartial firewall), scoped to a
// single equipment category via { clientId, category }. The general tab
// substitutes the scoped payload's filter_table for its server-mode one while
// a category is selected (ruling R6.3).

import { useQuery } from "@tanstack/react-query";
import { getFirebaseIdToken } from "@/shared/integrations/firebase/client";
import { captureEdgeFailure } from "@/shared/integrations/sentry/reportEdgeFailure";
import {
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
} from "@/shared/integrations/supabase/client";
import type { DashboardSpec } from "../components/types";

const ENDPOINT = "dashboard-data";

export function useCategoryScopeData(
  clientId: string | null,
  category: string | null,
) {
  return useQuery({
    queryKey: ["dashboard-data", clientId, "category", category],
    enabled: !!clientId && category != null,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<DashboardSpec> => {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Please sign in to view the dashboard.");

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/${ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ clientId, category }),
      });

      if (!resp.ok) {
        captureEdgeFailure({ endpoint: ENDPOINT, status: resp.status });
        throw new Error(`Dashboard data request failed (HTTP ${resp.status})`);
      }

      const spec = (await resp.json()) as DashboardSpec;
      if (!Array.isArray(spec?.sections)) {
        throw new Error("Malformed dashboard response");
      }
      return spec;
    },
  });
}
